// Voice/language matching shared by the agent and persona forms.
//
// Vendor voice catalogs (Cartesia, ElevenLabs) tag each voice with a language
// like "en" or "it". The forms filter the dropdown by the configured language,
// but nothing used to re-pick the *selected* value — so an agent switched to
// Italian kept its hardcoded English voice and the test caller bot spoke
// English at an Italian agent.

/** Normalize "IT-IT" / "it_IT" / "it" → "it". Returns "" when absent. */
export function toLangCode(language) {
  if (!language) return "";
  return String(language).split(/[-_]/)[0].toLowerCase();
}

/** Voices whose language tag matches `langCode`. */
export function voicesForLanguage(voices, langCode) {
  if (!Array.isArray(voices)) return [];
  if (!langCode) return voices;
  return voices.filter((v) =>
    String(v?.language || "").toLowerCase().startsWith(langCode)
  );
}

/**
 * Choose the voice id the form should hold for `langCode`.
 *
 * Returns `currentId` unchanged when it already speaks the language (so a
 * deliberate choice is never clobbered) or when the vendor has no voice for
 * that language (better a working English voice than an empty required field).
 *
 * `replaceExisting: false` narrows that to "only ever fill a blank". Use it on
 * an EXISTING agent: the stored voice is the operator's, so re-deriving it
 * turned opening-and-saving an agent into a silent swap to the language
 * default. On a new agent the value is still ours to track against the
 * language the user is picking, so the default stays true.
 */
export function pickVoiceForLanguage(
  voices,
  langCode,
  currentId,
  { replaceExisting = true } = {}
) {
  if (!replaceExisting && currentId) return currentId;

  const inLanguage = voicesForLanguage(voices, langCode);
  if (!inLanguage.length) return currentId;
  if (currentId && inLanguage.some((v) => v.id === currentId)) return currentId;
  return inLanguage[0].id;
}
